/**
 * Typed GraphQL client for the partner portal.
 *
 * Operations are written with `graphql()` from gql.tada, which type-checks every
 * field selection against the backend's generated SDL
 * (`reloaded-rest/schema.graphql`, wired up in `tsconfig.json` +
 * `src/graphql-env.d.ts`). Selecting a field that doesn't exist — or misusing a
 * variable — is a compile-time error, and `ResultOf<typeof doc>` gives the exact
 * response shape with no hand-written interfaces.
 *
 * Transport mirrors `src/lib/auth.ts`: authenticated POSTs to the pop.in GraphQL
 * endpoint, carrying the Firebase ID token and the `mighty` app id.
 */

import { print } from 'graphql';
import { initGraphQLTada, type TadaDocumentNode } from 'gql.tada';
import { getIdToken } from 'firebase/auth';

import type { introspection } from '@/graphql-env.d.ts';
import { GRAPHQL_ENDPOINT } from '@/config';
import { getFirebaseAuth } from '@/lib/firebase';
import { BackendUnavailableError } from '@/lib/backend-error';

/**
 * The `graphql()` document builder, bound to the backend schema. Custom scalars
 * are mapped to their TS representation (the backend's `Timestamp` is epoch ms).
 */
export const graphql = initGraphQLTada<{
  introspection: introspection;
  // App code (not a library) — unmask fragments so composed selections are
  // directly readable on the result without `readFragment`.
  disableMasking: true;
  scalars: {
    Timestamp: number;
    ID: string;
  };
}>();

export type { FragmentOf, ResultOf, VariablesOf } from 'gql.tada';

/** Error thrown when the GraphQL response carries an `errors` array. */
export class GraphQLRequestError extends Error {
  constructor(message: string, public readonly errors: Array<{ message: string }>) {
    super(message);
    this.name = 'GraphQLRequestError';
  }
}

interface GraphQLResponse<T> {
  data?: T;
  errors?: Array<{ message: string }>;
}

/**
 * The one authenticated POST both {@link gqlRequest} and {@link gqlRawRequest}
 * go through — carries the Firebase ID token + the `mighty` app id, unwraps the
 * `{ data, errors }` envelope. Takes an already-printed query string so it can
 * serve both a typed gql.tada document and a raw introspection query.
 */
async function postGraphQL<Result>(query: string, variables: Record<string, unknown>): Promise<Result> {
  if (!GRAPHQL_ENDPOINT) {
    throw new Error('BUN_PUBLIC_GRAPHQL_ENDPOINT is not configured.');
  }

  const auth = getFirebaseAuth();
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('Must be signed in to call the pop.in GraphQL API.');
  }
  const token = await getIdToken(currentUser);

  let response: Response;
  try {
    response = await fetch(GRAPHQL_ENDPOINT, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        authorization: token,
        // Identifies the calling pop.in client app, matching the public GraphQL
        // contract the admin-gui uses.
        'X-App-Id': 'mighty',
      },
      body: JSON.stringify({ query, variables }),
    });
  } catch (error) {
    throw new BackendUnavailableError('Could not reach the pop.in GraphQL endpoint.', { cause: error });
  }

  if (!response.ok) {
    throw new BackendUnavailableError(`GraphQL request failed (HTTP ${response.status}).`);
  }

  const body = (await response.json()) as GraphQLResponse<Result>;
  if (body.errors?.length) {
    throw new GraphQLRequestError(body.errors.map((e) => e.message).join(', '), body.errors);
  }
  if (!body.data) {
    throw new Error('GraphQL response contained no data.');
  }
  return body.data;
}

/**
 * Execute a typed operation. The result type is inferred from the document, so
 * callers get a fully-typed `data` with no casting.
 *
 * Variables are required iff the operation declares them.
 */
export async function gqlRequest<Result, Variables>(
  document: TadaDocumentNode<Result, Variables>,
  ...[variables]: Variables extends Record<string, never> ? [] : [Variables]
): Promise<Result> {
  return postGraphQL<Result>(print(document), (variables ?? {}) as Record<string, unknown>);
}

/**
 * Execute a RAW (untyped) query string over the same authenticated transport.
 * gql.tada types documents against the compile-time schema snapshot, so it can't
 * express a runtime capability probe (introspecting whether the LIVE backend has
 * a field the local schema doesn't yet know about). This escape hatch is for that
 * one job — see `src/data/theme-capabilities.ts`. Prefer {@link gqlRequest} for
 * everything the schema already covers.
 */
export async function gqlRawRequest<Result = unknown>(
  query: string,
  variables: Record<string, unknown> = {},
): Promise<Result> {
  return postGraphQL<Result>(query, variables);
}
